import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";

interface MentorMatch {
  id: string;
  mentee_id: string;
  mentor_id: string;
  status: string;
  message: string;
  created_at: string;
  mentee_profile?: { username: string };
  mentor_profile?: { username: string };
}

const AdminMentorship = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState<MentorMatch[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/auth");
      return;
    }

    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .single();

    if (!roleData) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
      navigate("/dashboard");
      return;
    }

    setIsAdmin(true);
    loadMatches();
  };

  const loadMatches = async () => {
    const { data, error } = await supabase
      .from("mentor_matches")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Error loading matches",
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Fetch profiles separately
    if (data) {
      const menteeIds = [...new Set(data.map(m => m.mentee_id))];
      const mentorIds = [...new Set(data.map(m => m.mentor_id))];
      const allIds = [...new Set([...menteeIds, ...mentorIds])];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, username")
        .in("id", allIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      const enrichedMatches = data.map(match => ({
        ...match,
        mentee_profile: profileMap.get(match.mentee_id),
        mentor_profile: profileMap.get(match.mentor_id),
      }));

      setMatches(enrichedMatches as MentorMatch[]);
    }
    
    setLoading(false);
  };

  const updateMatchStatus = async (matchId: string, newStatus: string) => {
    const { error } = await supabase
      .from("mentor_matches")
      .update({ status: newStatus })
      .eq("id", matchId);

    if (error) {
      toast({
        title: "Error updating status",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Status updated",
        description: `Match status changed to ${newStatus}`,
      });
      loadMatches();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="container mx-auto p-6">
      <Button
        variant="ghost"
        onClick={() => navigate("/dashboard")}
        className="mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Dashboard
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold">Mentorship Management</h1>
        <p className="text-muted-foreground">Manage all mentorship matches and requests</p>
      </div>

      <div className="grid gap-4">
        {matches.map((match) => (
          <Card key={match.id}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>
                  {match.mentee_profile?.username} → {match.mentor_profile?.username}
                </span>
                <span className={`text-sm px-3 py-1 rounded-full ${
                  match.status === 'accepted' ? 'bg-green-100 text-green-800' :
                  match.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {match.status}
                </span>
              </CardTitle>
              <CardDescription>
                Requested on {new Date(match.created_at).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm">{match.message || "No message provided"}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => updateMatchStatus(match.id, "accepted")}
                  disabled={match.status === "accepted"}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => updateMatchStatus(match.id, "declined")}
                  disabled={match.status === "declined"}
                >
                  Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {matches.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">No mentorship requests found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminMentorship;
